import { WebLogger, WebComponentCode, getCurrentPageLoadId } from '../logging';
import { getCurrentSessionId } from '../user-actions/sessionContext';

// ── Types ────────────────────────────────────────────────────────────

// Inlined to avoid pulling in @padcanvas/shared from shared-ui
interface ErrorDetail {
  detailString?: string;
  detailWord?: string;
}

// Generic server→client message piggybacked on responses (see
// docs/FeatureDesigns/duels-invite-smoothing-plan.md). Kept in sync with
// @padcanvas/shared's ServerMessage.
export interface ServerMessage<P = unknown> {
  id: string;
  type: string;
  payload: P;
  ts: string;
}

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string | ErrorDetail;
  requestId?: string;
  meta?: { messages?: ServerMessage[] };
}

export interface ApiClient {
  getToken: () => string | null;
  setToken: (token: string) => void;
  removeToken: () => void;
  getAuthHeaders: () => HeadersInit;
  fetchApi: <T>(endpoint: string, options?: RequestInit) => Promise<T>;
}

// ── HTTP client ──────────────────────────────────────────────────────

// Production URL, proxied to dev backend in local dev
export const API_BASE = import.meta.env.PROD
  ? (import.meta.env.VITE_API_BASE || 'https://aitutor-prod-func.azurewebsites.net/api')
  : '/api';

const apiLogger = new WebLogger(WebComponentCode.WEB_API, 'shared-api');

function getErrorMessage(error: string | ErrorDetail | undefined): string {
  if (!error) return 'An error occurred';
  if (typeof error === 'string') return error;
  return error.detailString || error.detailWord || 'An error occurred';
}

function getErrorDetailWord(error: string | ErrorDetail | undefined): string | undefined {
  if (!error || typeof error === 'string') return undefined;
  return error.detailWord;
}

/** Error thrown by API helpers. Carries the backend's `detailWord` so callers
 *  can branch on specific error codes (e.g. USER_NOT_FOUND) without parsing
 *  the human-readable message. */
export class ApiError extends Error {
  readonly detailWord?: string;
  /** The backend error's `context` block, when it carries one. Lets a caller act
   *  on structured detail (e.g. WRONG_TENANT_URL's `signInUrl`) instead of
   *  parsing the message. */
  readonly context?: Record<string, unknown>;
  readonly status?: number;
  constructor(message: string, opts?: { detailWord?: string; status?: number; context?: Record<string, unknown> }) {
    super(message);
    this.name = 'ApiError';
    this.context = opts?.context;
    this.detailWord = opts?.detailWord;
    this.status = opts?.status;
  }
}

// Server→client message bus (piggyback). Ids received on any response are
// queued here and flushed as the `X-Msg-Ack` header on the next outgoing request
// (at-least-once — the server stamps clientDeliveredAt on ack). Transport
// (piggyback today) is swappable; consumers subscribe to the `server-messages`
// window event. See docs/FeatureDesigns/duels-invite-smoothing-plan.md.
const pendingAckIds = new Set<string>();

/** Drain queued ack ids into a header value (and clear them). */
export function takeMessageAckHeader(): string | undefined {
  if (pendingAckIds.size === 0) return undefined;
  const value = Array.from(pendingAckIds).slice(0, 50).join(',');
  pendingAckIds.clear();
  return value;
}

function handleServerMessages<T>(json: ApiResponse<T>): void {
  const messages = json.meta?.messages;
  if (!messages?.length || typeof window === 'undefined') return;
  for (const m of messages) pendingAckIds.add(m.id);
  window.dispatchEvent(new CustomEvent('server-messages', { detail: messages }));
}

export async function handleResponse<T>(response: Response): Promise<T> {
  const json = await response.json().catch(() => ({
    success: false,
    error: 'An error occurred',
  })) as ApiResponse<T>;

  handleServerMessages(json);

  if (!response.ok || !json.success) {
    const msg = getErrorMessage(json.error);
    const detailWord = getErrorDetailWord(json.error);
    apiLogger.warn(`API error ${response.status}: ${msg}`, {
      status: response.status,
      url: response.url,
      requestId: json.requestId,
    });
    throw new ApiError(msg, {
      detailWord,
      status: response.status,
      context: (json.error && typeof json.error === 'object' && 'context' in json.error)
        ? (json.error as { context?: Record<string, unknown> }).context
        : undefined,
    });
  }

  apiLogger.debug(`API OK ${response.status}`, { url: response.url });
  return json.data as T;
}

/**
 * Which app is calling. Stamped on every request as `x-app-source`, so the
 * backend can tell a student from a teacher from a science-site reader — it
 * cannot otherwise, and that is how 107 zitmus readers came to be filed as
 * student-app activity. It also gives tier-2 telemetry the dimension the
 * admin Usage tab filters by.
 */
export type AppSource = 'web-student' | 'web-teacher' | 'web-admin';

export function createApiClient(tokenKey: string, appSource?: AppSource): ApiClient {
  return {
    getToken: () => localStorage.getItem(tokenKey),
    setToken: (token: string) => localStorage.setItem(tokenKey, token),
    removeToken: () => localStorage.removeItem(tokenKey),

    getAuthHeaders: (): HeadersInit => {
      const token = localStorage.getItem(tokenKey);
      // The sitting this call belongs to. A third of our telemetry is written
      // server-side from handlers that would otherwise have no idea which
      // sitting they are part of. Absent when no recorder is running.
      const sessionId = getCurrentSessionId();
      return {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(sessionId ? { 'x-session-id': sessionId } : {}),
        ...(appSource ? { 'x-app-source': appSource } : {}),
      };
    },

    fetchApi: async <T,>(endpoint: string, options?: RequestInit): Promise<T> => {
      const token = localStorage.getItem(tokenKey);
      if (!token) throw new Error('Not authenticated');

      // For multipart uploads the browser must set Content-Type (with the
      // boundary) itself — never force application/json on a FormData body.
      const isFormData = typeof FormData !== 'undefined' && options?.body instanceof FormData;
      const ackHeader = takeMessageAckHeader();
      // Correlate this request to the current client page load (perf attribution).
      const pageLoadId = getCurrentPageLoadId();
      const response = await fetch(`${API_BASE}${endpoint}`, {
        ...options,
        headers: {
          Authorization: `Bearer ${token}`,
          ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
          ...(ackHeader ? { 'X-Msg-Ack': ackHeader } : {}),
          ...(pageLoadId ? { 'x-page-load-id': pageLoadId } : {}),
          ...(getCurrentSessionId() ? { 'x-session-id': getCurrentSessionId()! } : {}),
          ...(appSource ? { 'x-app-source': appSource } : {}),
          ...options?.headers,
        },
      });

      if (response.status === 401) {
        localStorage.removeItem(tokenKey);
        window.dispatchEvent(new CustomEvent('session-expired'));
        throw new Error('Session expired');
      }

      return handleResponse<T>(response);
    },
  };
}

// ── JWT helpers ──────────────────────────────────────────────────────
// Client-side JWT inspection for UI hints (expiry, impersonation).
// NEVER use for auth decisions — the server always re-validates.

export function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    return JSON.parse(atob(token.split('.')[1]));
  } catch {
    return null;
  }
}

export function isTokenValid(token: string | null): boolean {
  if (!token) return false;
  const payload = decodeJwtPayload(token);
  if (!payload || typeof payload.exp !== 'number') return false;
  return payload.exp * 1000 > Date.now();
}

export function getImpersonatedBy(token: string | null): string | null {
  if (!token) return null;
  const payload = decodeJwtPayload(token);
  const impersonatedBy = payload?.impersonatedBy;
  return typeof impersonatedBy === 'string' ? impersonatedBy : null;
}

// ── App URL helpers ──────────────────────────────────────────────────

/** Get the student app base URL derived from current hostname */
export function getStudentAppUrl(): string {
  const host = window.location.hostname;
  if (host === 'localhost' || host === '127.0.0.1') return 'http://localhost:5001';
  // admin.hindaun.in → student.hindaun.in, teacher.buzzbrain.ai → student.buzzbrain.ai
  const studentHost = host.replace(/^(admin|teacher)\./, 'student.');
  return `${window.location.protocol}//${studentHost}`;
}

/** Get the teacher app base URL derived from current hostname */
export function getTeacherAppUrl(): string {
  const host = window.location.hostname;
  if (host === 'localhost' || host === '127.0.0.1') return 'http://localhost:7001';
  // admin.hindaun.in → teacher.hindaun.in, student.buzzbrain.ai → teacher.buzzbrain.ai
  const teacherHost = host.replace(/^(admin|student)\./, 'teacher.');
  return `${window.location.protocol}//${teacherHost}`;
}

// ── Tenant (branding) ────────────────────────────────────────────────

export interface MyTenant {
  id: string;
  name: string;
  type: string;
  status: string;
  /** T3c branding. `logoUrl` absent → no mark is shown for a branded tenant;
   *  see `tenantBranding`. */
  branding?: { logoUrl?: string };
}

/**
 * What the app shell should show as its mark, for one tenant.
 *
 * 'public' is the default tenant every unaffiliated user lands in, and
 * 'platform' is the super-admin's synthetic one. Neither is a customer, so both
 * keep the BuzzBrain wordmark — their names ("Public", "Platform") are internal
 * labels and would read as nonsense in the sidebar.
 *
 * A real tenant identifies itself: its logo when it has uploaded one, its name
 * underneath either way. No logo means no image rather than a stand-in — a
 * default mark on a tenant's sidebar misattributes whose product it is.
 *
 * Shared so all three app shells brand identically.
 */
export function tenantBranding(tenant?: MyTenant): { logoUrl?: string; brandName?: string } {
  if (!tenant || tenant.type === 'public' || tenant.type === 'platform') return {};
  return { logoUrl: tenant.branding?.logoUrl, brandName: tenant.name };
}

/** Fetch the caller's own tenant (GET /me/tenant) — used by the app shells to
 *  show tenant branding (logo). Shared so all three apps stay in sync. */
export async function fetchMyTenant(client: ApiClient): Promise<MyTenant> {
  const response = await fetch(`${API_BASE}/me/tenant`, { headers: client.getAuthHeaders() });
  return handleResponse<MyTenant>(response);
}

/**
 * Branding for the tenant an invited email belongs to (GET /auth/invite-branding).
 *
 * Sign-up screens run before there is a token, so they cannot use
 * `fetchMyTenant`. The invite already tied the email to its tenant, so the
 * email is the whole request; the server answers only for an invite still
 * pending and returns `{}` for everything else, which callers can spread
 * exactly like `tenantBranding(...)`.
 */
export async function fetchInviteBranding(email: string): Promise<{ logoUrl?: string; brandName?: string }> {
  const response = await fetch(`${API_BASE}/auth/invite-branding?email=${encodeURIComponent(email)}`);
  const { name, logoUrl } = await handleResponse<{ name?: string; logoUrl?: string }>(response);
  return name ? { brandName: name, logoUrl } : {};
}

// ── React Context ────────────────────────────────────────────────────
// Re-exported from ApiContext.tsx (separate file because it uses JSX)

export { ApiProvider, useApi } from './ApiContext';

export { waitForAsyncJob, AsyncJobFailedError, AsyncJobWaitTimeoutError } from './waitForAsyncJob';
export type { PolledJobView, WaitForAsyncJobOptions } from './waitForAsyncJob';
